import { useContext, useMemo } from 'react';

import { useNavigation } from '@atom/hooks/useNavigation';
import { useSchemas } from '@atom/hooks/useSchemas';
import { WorkOrderType } from '@atom/types/work';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import { numberToLocaleString } from '@atom/utilities/currencyUtility';
import {
  getLocalDisplayDate,
  setDisplayDate,
  setDurationDisplay,
} from '@atom/utilities/timeUtilities';

import WorkOrderContext from '../WorkOrderContext';

import classes from './workOrderDetailHeader.module.css';

const HeaderInfoSection = () => {
  const [, { navigate }] = useNavigation();
  const { workOrderDetail } = useContext(WorkOrderContext);
  const { schemas = [] } = useSchemas();

  const inventorySchemaName = useMemo(() => {
    const foundSchema = schemas.find(
      schema => schema.id === workOrderDetail?.inventoryAssetSchemaId,
    );

    return foundSchema?.name || '-';
  }, [workOrderDetail?.inventoryAssetSchemaId, schemas]);

  const navigateToInventoryDetail = () =>
    navigate(`/inventory/${workOrderDetail.inventoryAssetId}`);

  const inventoryNameOnClick = workOrderDetail?.inventoryAssetId
    ? navigateToInventoryDetail
    : () => {};

  const showActualCost = hasRolePermissions(ROLE_SETS.MANAGER);
  const showInventoryInfo =
    workOrderDetail?.inventoryAssetSchemaId &&
    workOrderDetail?.type === WorkOrderType.DEFAULT;

  return (
    <div className={classes.headerInfoSection}>
      {showInventoryInfo && (
        <div className={classes.infoSectionBlock}>
          <div className={classes.infoLabelContainer}>
            <div>Asset Name:</div>
            <div>Asset Type:</div>
          </div>
          <div>
            <div
              className={classes.inventoryName}
              onClick={inventoryNameOnClick}
            >
              {workOrderDetail?.inventoryAssetName || '-'}
            </div>
            <div className={classes.inventoryName}>{inventorySchemaName}</div>
          </div>
        </div>
      )}
      {workOrderDetail?.appointmentsEnabled ? (
        <>
          <div className={classes.infoSectionBlock}>
            <div className={classes.infoLabelContainer}>
              <div>Planned Start Date:</div>
              <div>Due Date:</div>
            </div>
            <div>
              <div>
                {getLocalDisplayDate(
                  workOrderDetail.plannedStartDate,
                  'MM/dd/yyyy hh:mm a',
                  false,
                ) || '-'}
              </div>
              <div>
                {getLocalDisplayDate(
                  workOrderDetail.dueDate,
                  'MM/dd/yyyy hh:mm a',
                  false,
                ) || '-'}
              </div>
            </div>
          </div>
          <div className={classes.infoSectionBlock}>
            <div className={classes.infoLabelContainer}>
              <div>Start Date:</div>
              <div>Completed Date:</div>
            </div>
            <div>
              <div>{setDisplayDate(workOrderDetail.startTime)}</div>
              <div>{setDisplayDate(workOrderDetail.completionDate)}</div>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className={classes.infoSectionBlock}>
            <div className={classes.infoLabelContainer}>
              <div>Start Date:</div>
              <div>Completed Date:</div>
            </div>
            <div>
              <div>{setDisplayDate(workOrderDetail.startTime)}</div>
              <div>{setDisplayDate(workOrderDetail.completionDate)}</div>
            </div>
          </div>
          <div className={classes.infoSectionBlock}>
            <div className={classes.infoLabelContainer}>
              <div>Due Date:</div>
              <div>Duration:</div>
            </div>
            <div>
              <div>{setDisplayDate(workOrderDetail.dueDate)}</div>
              <div>{setDurationDisplay(workOrderDetail.duration)}</div>
            </div>
          </div>
        </>
      )}

      <div className={classes.infoSectionBlock}>
        <div className={classes.infoLabelContainer}>
          <div>Estimated Cost:</div>
          {showActualCost && <div>Actual Cost:</div>}
        </div>
        <div>
          <div>{numberToLocaleString(workOrderDetail.estimatedCost)}</div>
          {showActualCost && (
            <div>{numberToLocaleString(workOrderDetail.actualCost)}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HeaderInfoSection;
